/**
 * CausalTraceView — the "Why?" affordance (Normal Core §6.5.8, Phase 8). Given
 * an event's `causation_uid` ancestry (resolved server-side via the core
 * `causalChain` helper / the /api/events/chain contract), it renders the chain
 * ROOT-CAUSE FIRST … → the anchor event as a vertical, numbered flow:
 *
 *     signup → assignment → recommendation → action
 *
 * so a Play card, attention item, or agent action can answer "why am I being
 * shown this / why did the agent do that". Pure presentation: rows are
 * pre-fetched `EventTimelineRow`s (same shape as EventTimeline), so the block
 * never sees a raw envelope. The final row is the anchor (what you clicked
 * "Why?" on); earlier rows are its causes, oldest first.
 */

import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { IntentBadge } from "./status-badge";
import { formatRelativeTime } from "../format";
import type { EventTimelineRow } from "./event-timeline";

export interface CausalTraceViewProps {
  /** The causal chain, ROOT cause first … → the anchor event (last). */
  chain: EventTimelineRow[];
  title?: string;
  /** Default true; pass false inside a Card that owns its own header. */
  wrapInCard?: boolean;
  emptyMessage?: React.ReactNode;
  className?: string;
}

export function CausalTraceView({
  chain,
  title = "Why?",
  wrapInCard = true,
  emptyMessage = "No causal trace available.",
  className,
}: CausalTraceViewProps) {
  const body =
    chain.length === 0 ? (
      <p className="px-6 py-4 type-subtext text-muted-foreground">{emptyMessage}</p>
    ) : (
      <ol className="px-6">
        {chain.map((row, i) => {
          const isAnchor = i === chain.length - 1;
          const isRoot = i === 0;
          return (
            <li
              key={row.uid}
              className="relative flex gap-3 border-b border-border/40 py-3 first:pt-0 last:border-b-0"
            >
              {/* step index + connector — reads as a flow, not a flat list */}
              <div className="flex flex-col items-center">
                <span
                  className={
                    "flex size-5 shrink-0 items-center justify-center rounded-full type-subtext font-medium " +
                    (isAnchor
                      ? "bg-foreground text-background"
                      : "bg-muted text-muted-foreground")
                  }
                >
                  {i + 1}
                </span>
                {!isAnchor ? (
                  <span className="mt-1 w-px flex-1 bg-border/60" aria-hidden />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <IntentBadge intent={row.isCritical ? "warning" : "neutral"}>
                    {row.type}
                  </IntentBadge>
                  {isRoot ? (
                    <span className="type-subtext text-muted-foreground">
                      root cause
                    </span>
                  ) : null}
                  {isAnchor && !isRoot ? (
                    <span className="type-subtext text-muted-foreground">
                      this
                    </span>
                  ) : null}
                </div>
                <p className="mt-0.5 truncate type-body text-foreground">
                  {row.summary ?? row.type}
                </p>
                <p className="type-subtext text-muted-foreground">
                  {(row.actorLabel ?? "System") + " · " + formatRelativeTime(row.occurredAt)}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    );

  if (!wrapInCard) return <div className={className}>{body}</div>;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="px-0">{body}</CardContent>
    </Card>
  );
}
